import authService from "@/services/auth/authService";
import { createSseParser, parseEventData } from "@/utils/ai/sse";

/**
 * The streaming send, read over `fetch`.
 *
 * Not axios, and not `EventSource`. `EventSource` is out because the endpoint is a `POST`
 * carrying the message in a body — a query string would put the text in access logs and
 * browser history, which for a workspace with a sensitive-data filter in front of it
 * would defeat the point of having one. Axios is out because XHR gives no usable
 * incremental reader; `fetch` gives a `ReadableStream`.
 *
 * Losing `EventSource` also loses its automatic reconnect, and that is the outcome we
 * want. There is no stream reattachment server-side — the pod holding the connection is
 * the pod talking to the provider for the whole answer — so a reconnect would silently
 * start a *second* generation and bill for it. Nothing in this file retries.
 *
 * Because it is not axios, none of the shared interceptors apply: the token has to be
 * attached here, and a refused request has to be reshaped into something
 * `describeApiError` can read. Both are done below rather than left to the caller.
 */

/** Where the request is refused before any generation starts, the token may just be old. */
const UNAUTHORISED = 401;

/**
 * Whether this browser can read a response body as it arrives.
 *
 * Checked before sending rather than after, because the answer to "can we stream?" must
 * not cost a generation. Where it is false the caller uses the non-streaming endpoint,
 * which the backend deliberately kept alive for exactly this.
 *
 * @returns {Boolean}
 */
export function supportsStreaming() {
  return (
    typeof window !== "undefined" &&
    typeof window.fetch === "function" &&
    typeof window.TextDecoder === "function"
  );
}

/**
 * Send a message and read the answer as it is generated.
 *
 * Resolves when the stream ends. Rejects only when the request never opened — anything
 * that goes wrong after `start` arrives as an `error` event, because by then no HTTP
 * status is available to carry it.
 *
 * @param {Object} options
 * @param {String} options.conversationId
 * @param {String} options.message - raw user text
 * @param {Number} options.skillId - the Skill governing this message, never a version
 * @param {Function} options.onStart - ({ conversationId, userMessage, assistantMessage, contextTruncated })
 * @param {Function} options.onDelta - ({ text }) — a fragment to APPEND; it is not cumulative
 * @param {Function} options.onDone - ({ assistantMessage }) — the answer as stored
 * @param {Function} options.onError - ({ code, message }) — it did not finish
 * @returns {Promise<void>}
 */
export async function streamMessage({
  conversationId,
  message,
  skillId,
  onStart,
  onDelta,
  onDone,
  onError,
}) {
  const response = await openStream(conversationId, { message, skillId });

  const emit = ({ event, data }) => {
    // A frame we cannot read is skipped rather than thrown on. One unreadable fragment
    // in the middle of an answer is not worth tearing the stream down for, and `done`
    // still carries the authoritative finished message.
    const payload = parseEventData(data);
    if (!payload) {
      return;
    }

    if (event === "start" && onStart) {
      onStart(payload);
    } else if (event === "delta" && onDelta) {
      onDelta(payload);
    } else if (event === "done" && onDone) {
      onDone(payload);
    } else if (event === "error" && onError) {
      onError(payload);
    }
    // Any other event name is ignored, so the backend can add one without this
    // breaking.
  };

  const parser = createSseParser(emit);
  const decoder = new TextDecoder();

  // `response.body` is absent on a few older engines that otherwise have `fetch`. The
  // request has already been sent and the answer is already being generated, so the
  // alternative to buffering it is throwing away work the user is being charged for.
  // The same parser reads it; only the incremental part is lost.
  if (!response.body || typeof response.body.getReader !== "function") {
    parser.feed(await response.text());
    parser.flush();
    return;
  }

  const reader = response.body.getReader();

  try {
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      // `stream: true` keeps a multi-byte character split across two chunks intact.
      parser.feed(decoder.decode(value, { stream: true }));
    }
    parser.feed(decoder.decode());
  } finally {
    parser.flush();
  }
}

/**
 * Open the stream, once, with one retry for a stale token.
 *
 * The retry is safe precisely here and nowhere else: a 401 is decided before any gate
 * runs, so nothing has been generated and resending cannot produce a second answer. It
 * exists because this request does not pass through the axios interceptor that refreshes
 * tokens for every other call in the app.
 */
async function openStream(conversationId, body) {
  let response = await postStream(conversationId, body, await authService.getToken());

  if (response.status === UNAUTHORISED) {
    const refreshed = await refreshToken();
    if (refreshed) {
      response = await postStream(conversationId, body, refreshed);
    }
  }

  if (!response.ok) {
    throw await asApiError(response);
  }

  return response;
}

function postStream(conversationId, body, token) {
  const headers = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetch(streamUrl(conversationId), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    credentials: "same-origin",
    // Explicit rather than default: a cached SSE response is meaningless, and some
    // proxies will serve one if not told otherwise.
    cache: "no-store",
  });
}

async function refreshToken() {
  try {
    const result = await authService.refreshBackendToken();
    return result && result.success ? result.token : null;
  } catch {
    // A failed refresh is not this function's problem to report: the retry is skipped,
    // the original 401 is thrown, and the axios interceptor handles the session on the
    // next ordinary call.
    return null;
  }
}

/**
 * Reshape a refused response into the shape `describeApiError` reads.
 *
 * Everything that can refuse a message is decided before the stream opens, so these are
 * ordinary JSON errors with real status codes — the workspace being off, a quota, the
 * sensitive-data filter, a pod shutting down. Giving them an axios-shaped `response`
 * means one function describes a refusal whichever transport carried it.
 */
async function asApiError(response) {
  let data = null;

  try {
    data = await response.json();
  } catch (parseError) {
    data = null;
  }

  const error = new Error((data && data.message) || `Request failed (${response.status})`);
  error.response = { status: response.status, data };
  return error;
}

/**
 * The absolute URL, built from the same base every other ARCH call uses.
 *
 * `fetch` resolves a relative path against the page rather than against
 * `axios.defaults.baseURL`, so the base has to be applied by hand here — and it is read
 * from the environment rather than from axios, whose shared default is rewritten by
 * whichever service called `init()` most recently.
 */
function streamUrl(conversationId) {
  const base = process.env.VUE_APP_ARCH_BASE_URL || "/api/v1/";
  const path = `ai/conversations/${encodeURIComponent(conversationId)}/messages/stream`;

  return base.endsWith("/") ? `${base}${path}` : `${base}/${path}`;
}

export default { streamMessage, supportsStreaming };
